import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { ConfirmationDialog } from '../components/ui/confirmation-dialog';
import { useTenantSettings } from '../hooks/useTenantSettings';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { TimePicker } from '../components/ui/time-picker';
import { Input } from '../components/ui/input';
import {
  Layers, ChevronDown, ChevronRight, Clock, ArrowRightLeft,
  Trash2, Loader2, ArrowDownToLine, ArrowUpFromLine, Truck, Wrench, Pencil
} from 'lucide-react';

const SetupGroupPanel = ({ api, job, onRefresh, allocations = [], equipment = [], attachments = [] }) => {
  const { fmtDateShort } = useTenantSettings();
  const [groupData, setGroupData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const [demobDialogOpen, setDemobDialogOpen] = useState(false);
  const [demobEndTime, setDemobEndTime] = useState('17:00');
  const [demobPreview, setDemobPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [excludedStepIds, setExcludedStepIds] = useState(new Set());
  const [timeOverrides, setTimeOverrides] = useState({}); // { step_id | "parent": "HH:MM" }
  const [generatingDemob, setGeneratingDemob] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', description: '', onConfirm: null });

  const groupId = job?.setup_group_id;
  const isParent = job?.is_setup_parent;

  useEffect(() => {
    if (groupId) fetchGroup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId]);

  const fetchGroup = async () => {
    if (!groupId) return;
    setLoading(true);
    try {
      const res = await api.get(`/setup-groups/${groupId}`);
      setGroupData(res.data);
    } catch (err) {
      console.error('Failed to fetch setup group', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchDemobPreview = async (endTime) => {
    setPreviewLoading(true);
    try {
      const params = endTime ? { end_time: endTime } : {};
      const res = await api.get(`/setup-groups/${groupId}/demob-preview`, { params });
      setDemobPreview(res.data);
      if (!endTime && res.data.default_end_time) {
        setDemobEndTime(res.data.default_end_time);
      }
      // Reset overrides when reloading (unless user already tweaked them)
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to preview demobilisation');
      setDemobPreview(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const openDemobDialog = async () => {
    setExcludedStepIds(new Set());
    setTimeOverrides({});
    setDemobDialogOpen(true);
    await fetchDemobPreview(null);
  };

  // Recompute preview when end_time changes (server re-computes per-step times)
  const handleEndTimeChange = async (newTime) => {
    setDemobEndTime(newTime);
    if (newTime && /^\d{2}:\d{2}$/.test(newTime)) {
      await fetchDemobPreview(newTime);
    }
  };

  const toggleExclude = (rowKey, isParentRow) => {
    if (isParentRow) return; // Parent cannot be excluded
    setExcludedStepIds(prev => {
      const next = new Set(prev);
      if (next.has(rowKey)) next.delete(rowKey);
      else next.add(rowKey);
      return next;
    });
  };

  const handleTimeOverride = (key, value) => {
    setTimeOverrides(prev => ({ ...prev, [key]: value }));
  };

  // Inline update a child job's time or description (Setup Group tab)
  const handleChildJobUpdate = async (childJob, patch) => {
    try {
      // Backend expects full job payload on PUT. Fetch fresh to avoid overwriting changes.
      const fresh = await api.get('/jobs');
      const full = fresh.data.find(j => j.id === childJob.id) || childJob;
      await api.put(`/jobs/${childJob.id}`, { ...full, ...patch });
      toast.success('Saved');
      await fetchGroup();
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save');
    }
  };

  // Find allocation for a specific child job (used for Load chips)
  const allocationForJob = (childJob) => {
    return (allocations || []).find(
      a => a.job_id === childJob.id &&
           a.start_date <= (childJob.start_date) &&
           ((a.end_date || a.start_date) >= childJob.start_date)
    );
  };

  const handleGenerateDemob = async () => {
    setGeneratingDemob(true);
    try {
      const res = await api.post(`/setup-groups/${groupId}/generate-demob`, {
        end_time: demobEndTime,
        excluded_step_ids: Array.from(excludedStepIds),
        time_overrides: timeOverrides,
      });
      toast.success(`Generated ${res.data.demob_jobs_created} demobilisation jobs`);
      setDemobDialogOpen(false);
      fetchGroup();
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate demobilisation');
    } finally {
      setGeneratingDemob(false);
    }
  };

  const handleDeleteGroup = async () => {
    setConfirmDialog({
      open: true,
      title: 'Delete Setup Group',
      description: 'Delete all child mobilisation/demobilisation jobs? The parent job will be kept.',
      onConfirm: async () => {
        setConfirmDialog(d => ({ ...d, open: false }));
        setDeleting(true);
        try {
          await api.delete(`/setup-groups/${groupId}`);
          toast.success('Setup group deleted');
          setGroupData(null);
          if (onRefresh) onRefresh();
        } catch (err) {
          toast.error(err.response?.data?.detail || 'Failed to delete setup group');
        } finally {
          setDeleting(false);
        }
      },
    });
  };

  if (!groupId) return null;

  // ----- Inline-editable child-job row (Setup Group tab) -----
  const ChildJobRow = ({ childJob, tone }) => {
    const toneClass = tone === 'demob'
      ? 'bg-amber-50 dark:bg-amber-950/20 border-amber-100 dark:border-amber-900/30'
      : 'bg-blue-50 dark:bg-blue-950/20 border-blue-100 dark:border-blue-900/30';
    const prefix = tone === 'demob' ? '[DEMOB] ' : '[MOB] ';
    const [editingTime, setEditingTime] = useState(false);
    const [localTime, setLocalTime] = useState(childJob.start_time || '07:00');
    const [descDraft, setDescDraft] = useState((childJob.description || '').replace(prefix, ''));
    const [descDirty, setDescDirty] = useState(false);

    const alloc = allocationForJob(childJob);
    const loadEqIds = alloc?.cargo_equipment_ids || [];
    const loadAttIds = alloc?.cargo_attachment_ids || [];
    const hasLoad = loadEqIds.length + loadAttIds.length > 0;

    const commitTime = async (newTime) => {
      setLocalTime(newTime);
      setEditingTime(false);
      if (newTime && newTime !== childJob.start_time) {
        await handleChildJobUpdate(childJob, { start_time: newTime });
      }
    };

    const commitDescription = async () => {
      if (!descDirty) return;
      const nextDesc = `${prefix}${descDraft}`;
      setDescDirty(false);
      if (nextDesc !== childJob.description) {
        await handleChildJobUpdate(childJob, { description: nextDesc });
      }
    };

    return (
      <div className={`p-2 rounded border ${toneClass}`} data-testid={`setup-child-row-${childJob.id}`}>
        <div className="flex items-center gap-2 text-sm">
          <span
            className="shrink-0 text-[10px] text-muted-foreground font-mono"
            data-testid={`setup-child-date-${childJob.id}`}
          >
            {childJob.start_date ? fmtDateShort(childJob.start_date) : ''}
          </span>
          {editingTime ? (
            <div className="shrink-0">
              <TimePicker
                value={localTime}
                onChange={commitTime}
                testId={`setup-child-time-${childJob.id}`}
              />
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setEditingTime(true)}
              className="shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-muted-foreground/30 font-mono text-xs hover:border-foreground hover:bg-muted/50 transition-colors"
              data-testid={`setup-child-time-btn-${childJob.id}`}
              title="Click to edit time"
            >
              <Clock className="h-3 w-3" />
              {childJob.start_time}
              <Pencil className="h-2.5 w-2.5 opacity-50" />
            </button>
          )}
          <span className="font-medium truncate max-w-[35%]">{childJob.equipment_type}</span>
          {childJob.transport_parent_job_id && (
            <Badge variant="outline" className="text-[9px] shrink-0 text-purple-600 dark:text-purple-400 border-purple-300 dark:border-purple-700">
              <Truck className="h-2.5 w-2.5 mr-0.5" /> transport
            </Badge>
          )}
          <Badge variant="secondary" className="text-xs shrink-0 ml-auto">
            #{childJob.job_number}
          </Badge>
        </div>
        {/* Day Work Description — editable */}
        <div className="mt-1.5 flex items-center gap-2">
          <Label className="text-[10px] text-muted-foreground shrink-0 uppercase tracking-wide">Work</Label>
          <Input
            type="text"
            value={descDraft}
            onChange={(e) => { setDescDraft(e.target.value); setDescDirty(true); }}
            onBlur={commitDescription}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); } }}
            placeholder="Describe the work for this step…"
            className="h-7 text-xs"
            data-testid={`setup-child-desc-${childJob.id}`}
          />
        </div>
        {/* Load summary (from allocation) */}
        {hasLoad && (
          <div className="mt-1.5 flex flex-wrap items-center gap-1">
            <Label className="text-[10px] text-muted-foreground shrink-0 uppercase tracking-wide">Load</Label>
            {loadEqIds.map(eid => {
              const e = equipment.find(x => x.id === eid);
              const label = e ? (e.asset_id || e.name) : eid.slice(0, 6);
              return (
                <span key={`le-${eid}`} className="text-[10px] bg-blue-500/20 text-blue-700 dark:text-blue-200 rounded px-1.5 py-0.5 border border-blue-400/30">
                  {label}
                </span>
              );
            })}
            {loadAttIds.map(aid => {
              const a = attachments.find(x => x.id === aid);
              const label = a ? (a.name || a.asset_id) : aid.slice(0, 6);
              return (
                <span key={`la-${aid}`} className="inline-flex items-center gap-1 text-[10px] bg-purple-500/20 text-purple-700 dark:text-purple-200 rounded px-1.5 py-0.5 border border-purple-400/30">
                  <Wrench className="h-2.5 w-2.5" /> {label}
                </span>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-4 flex items-center justify-center">
          <Loader2 className="h-4 w-4 animate-spin mr-2" />
          <span className="text-sm text-muted-foreground">Loading setup group...</span>
        </CardContent>
      </Card>
    );
  }

  if (!groupData) return null;

  const hasDemob = (groupData.demobilisation_jobs || []).length > 0;
  const previewRows = demobPreview?.rows || [];
  const transportLegCount = previewRows.filter(r => r.will_spawn_transport && !excludedStepIds.has(r.row_key)).length;
  const activeRowCount = previewRows.filter(r => r.is_parent || !excludedStepIds.has(r.row_key)).length;

  return (
    <>
      <Card data-testid="setup-group-panel">
        <CardHeader className="pb-2 cursor-pointer" onClick={() => setExpanded(!expanded)}>
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              <Layers className="h-4 w-4" />
              Setup Group
              {groupData.profile_name && (
                <Badge variant="outline" className="ml-1 font-normal">{groupData.profile_name}</Badge>
              )}
              <Badge variant="secondary">{groupData.total_jobs} jobs</Badge>
            </CardTitle>
            {isParent && (
              <div className="flex gap-1" onClick={e => e.stopPropagation()}>
                {!hasDemob && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={openDemobDialog}
                    data-testid="generate-demob-btn"
                  >
                    <ArrowRightLeft className="h-3 w-3 mr-1" />
                    Generate Demob
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleDeleteGroup}
                  disabled={deleting}
                  className="text-destructive"
                  data-testid="delete-setup-group-btn"
                >
                  <Trash2 className="h-3 w-3 mr-1" />
                  {deleting ? 'Deleting...' : 'Remove Group'}
                </Button>
              </div>
            )}
          </div>
        </CardHeader>

        {expanded && (
          <CardContent className="pt-0">
            {/* Mobilisation jobs */}
            {(groupData.mobilisation_jobs || []).length > 0 && (
              <div className="mb-3">
                <div className="flex items-center gap-2 mb-2">
                  <ArrowDownToLine className="h-3.5 w-3.5 text-blue-500" />
                  <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Mobilisation</span>
                </div>
                <div className="space-y-1">
                  {groupData.mobilisation_jobs.map(j => (
                    <ChildJobRow key={j.id} childJob={j} tone="mob" />
                  ))}
                </div>
              </div>
            )}

            {/* Demobilisation jobs */}
            {hasDemob && (
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <ArrowUpFromLine className="h-3.5 w-3.5 text-amber-500" />
                  <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Demobilisation</span>
                </div>
                <div className="space-y-1">
                  {groupData.demobilisation_jobs.map(j => (
                    <ChildJobRow key={j.id} childJob={j} tone="demob" />
                  ))}
                </div>
              </div>
            )}

            {/* Empty state for parent with no children */}
            {(groupData.mobilisation_jobs || []).length === 0 && !hasDemob && (
              <p className="text-sm text-muted-foreground text-center py-4">No child jobs in this group</p>
            )}
          </CardContent>
        )}
      </Card>

      {/* Demob dialog — editable step review */}
      <Dialog open={demobDialogOpen} onOpenChange={setDemobDialogOpen}>
        <DialogContent className="max-w-2xl" data-testid="demob-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowUpFromLine className="h-4 w-4 text-amber-500" />
              Generate Demobilisation
            </DialogTitle>
            <DialogDescription>
              Review and tweak each demob step. Parent asset leaves last; child assets demob in reverse-offset (LIFO) order.
              Transport legs (float trucks) are auto-spawned for heavy assets.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            {/* Last-day end time input */}
            <div className="grid grid-cols-3 gap-3 items-end">
              <div className="col-span-1">
                <Label>Job End Time</Label>
                <TimePicker
                  value={demobEndTime}
                  onChange={(v) => handleEndTimeChange(v)}
                  minuteStep={5}
                  className="w-full mt-1"
                  testId="demob-end-time-input"
                />
              </div>
              <div className="col-span-2 text-xs text-muted-foreground pb-2">
                Demob times are computed backwards from this time (parent leaves at end time).
                {demobPreview?.end_date && (
                  <span className="ml-1">All demob jobs land on <span className="font-mono font-medium">{demobPreview.end_date}</span>.</span>
                )}
              </div>
            </div>

            {/* Preview rows */}
            {previewLoading ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                <span className="text-sm text-muted-foreground">Calculating demob plan...</span>
              </div>
            ) : previewRows.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">No demob steps to generate.</p>
            ) : (
              <div className="border rounded-md divide-y max-h-[320px] overflow-y-auto" data-testid="demob-preview-rows">
                {previewRows.map((row, idx) => {
                  const isExcluded = !row.is_parent && excludedStepIds.has(row.row_key);
                  const effectiveTime = timeOverrides[row.is_parent ? 'parent' : row.row_key] ?? row.time;
                  return (
                    <div
                      key={row.row_key}
                      className={`flex items-center gap-3 p-2.5 text-sm ${isExcluded ? 'opacity-40 bg-muted/30' : ''} ${row.is_parent ? 'bg-amber-50/50 dark:bg-amber-950/10' : ''}`}
                      data-testid={`demob-row-${row.row_key}`}
                    >
                      <Checkbox
                        checked={!isExcluded}
                        disabled={row.is_parent}
                        onCheckedChange={() => toggleExclude(row.row_key, row.is_parent)}
                        aria-label={`Include step ${row.label}`}
                        data-testid={`demob-include-${row.row_key}`}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium truncate">
                            {idx + 1}. {row.label || row.equipment_type}
                          </span>
                          {row.is_parent && <Badge variant="outline" className="text-[9px]">Parent job · anchor time only</Badge>}
                          {row.will_spawn_transport && (
                            <Badge variant="outline" className="text-[9px] text-purple-600 dark:text-purple-400 border-purple-300 dark:border-purple-700">
                              <Truck className="h-2.5 w-2.5 mr-0.5" /> + float truck
                            </Badge>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground truncate">
                          {row.equipment_type}
                          {row.offset_minutes !== 0 && (
                            <span className="ml-2">offset {Math.abs(row.offset_minutes)}m {row.offset_minutes < 0 ? 'before' : 'after'} end</span>
                          )}
                        </div>
                      </div>
                      <TimePicker
                        value={effectiveTime}
                        disabled={isExcluded}
                        onChange={(v) => handleTimeOverride(row.is_parent ? 'parent' : row.row_key, v)}
                        minuteStep={5}
                        className="w-24"
                        testId={`demob-time-${row.row_key}`}
                      />
                    </div>
                  );
                })}
              </div>
            )}

            {/* Summary */}
            {previewRows.length > 0 && (
              <div className="text-xs text-muted-foreground flex items-center gap-4">
                <span><span className="font-medium text-foreground">{activeRowCount}</span> demob job{activeRowCount === 1 ? '' : 's'}</span>
                {transportLegCount > 0 && (
                  <span className="flex items-center gap-1">
                    <Truck className="h-3 w-3" />
                    <span className="font-medium text-foreground">{transportLegCount}</span> auto transport leg{transportLegCount === 1 ? '' : 's'}
                  </span>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDemobDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleGenerateDemob}
              disabled={generatingDemob || previewLoading || previewRows.length === 0}
              data-testid="confirm-demob-btn"
            >
              {generatingDemob ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Generating...</>
              ) : (
                `Generate ${activeRowCount} Demob Job${activeRowCount === 1 ? '' : 's'}`
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );

      <ConfirmationDialog
        open={confirmDialog.open}
        onOpenChange={(open) => setConfirmDialog(d => ({ ...d, open }))}
        title={confirmDialog.title}
        description={confirmDialog.description}
        onConfirm={confirmDialog.onConfirm}
        confirmText="Confirm"
      />
};

export default SetupGroupPanel;
